import { useCallback, useMemo, useState } from "react";
import {
  Container,
  Autocomplete,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Typography,
  Chip,
  Grid,
  TablePagination,
  InputAdornment,
  Stack,
} from "@mui/material";
import useApiEp from "../../hooks/useApiEp";
import DateRangePicker from "../../widgets/DateRangePicker";
import dayjs from "dayjs";
import { date } from "yup";
import SearchIcon from "@mui/icons-material/Search";
import { debounce } from "lodash";
import CampaignStatsPanel from "./AnalysisCom";
import { useNavigate } from "react-router-dom";
import CustomPagination from "./CustomPagination";
function buildMessagesUrl(
  customerId,
  campaign,
  phoneQuery,
  page,
  limit,
  dateRange
) {
  const baseUrl = `/api/messages/${encodeURIComponent(customerId)}`;
  const params = new URLSearchParams();

  // Required parameters
  if (campaign) {
    params.append("campaignName", encodeURIComponent(campaign));
  }

  // Optional parameters
  if (phoneQuery) {
    params.append("phoneQ", encodeURIComponent(phoneQuery));
  }

  // Pagination (always included)
  params.append("page", page + 1);
  params.append("limit", limit);

  // Date range (optional)
  if (dateRange?.startDate && dateRange?.endDate) {
    params.append("startDate", encodeURIComponent(dateRange.startDate));
    params.append("endDate", encodeURIComponent(dateRange.endDate));
  }

  return `${baseUrl}?${params.toString()}`;
}
const TrackCampaign = () => {
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [phoneS, setPhoneS] = useState("");
  const [debounceSq, setDebounceSq] = useState("");
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
  });
  const navigate = useNavigate();
  const debouncedPhoneS = useCallback(
    debounce((value) => {
      setDebounceSq(value);
    }, 300),
    []
  );
  const {
    data: customerIds = [],
    isLoading: loadingCustomers,
    isError: errorCustomers,
    error: customerError,
  } = useApiEp("/api/customer-ids", true);

  const {
    data: campaigns = [],
    isLoading: loadingCampaigns,
    isError: errorCampaigns,
    error: campaignError,
  } = useApiEp(
    selectedCustomerId
      ? `/api/campaigns/${selectedCustomerId}`
      : "/api/campaigns",
    !!selectedCustomerId
  );
  //campaign-reply-count

  const {
    data: replyData,
    isLoading: loadingReplyCount,
    isError: errorReplyCount,
    error: replyCountError,
  } = useApiEp(
    `/api/campaign-reply-count/${selectedCustomerId}?campaignName=${selectedCampaign}`,
    !!selectedCustomerId && !!selectedCampaign
  );

  const isDateRangeS = useMemo(
    () => dateRange.endDate && dateRange.startDate,
    [dateRange]
  );

  const {
    data: messagesData = {
      data: [],
      meta: { total: 0, page: 1, limit: 10, pages: 1 },
    },
    isLoading: loadingMessages,
    isError: errorMessages,
    error: messageError,
  } = useApiEp(
    selectedCustomerId && (selectedCampaign || isDateRangeS)
      ? (() => {
          // Validate inputs
          if (!selectedCustomerId) return null;

          // Build query parameters using URLSearchParams
          const params = new URLSearchParams();
          if (selectedCampaign) {
            params.append("campaignName", encodeURIComponent(selectedCampaign));
          }
          if (debounceSq) {
            params.append("phoneQ", encodeURIComponent(debounceSq));
          }
          params.append("page", String(page + 1));
          params.append("limit", String(rowsPerPage));
          if (isDateRangeS && dateRange?.startDate && dateRange?.endDate) {
            params.append("startDate", encodeURIComponent(dateRange.startDate));
            params.append("endDate", encodeURIComponent(dateRange.endDate));
          }

          // Construct URL
          return `/api/messages/${encodeURIComponent(
            selectedCustomerId
          )}?${params.toString()}`;
        })()
      : null,
    !!(selectedCustomerId && (selectedCampaign || isDateRangeS))
  );

  const handleCustomerSelect = (event, value) => {
    setSelectedCustomerId(value);
    setSelectedCampaign(null);
    setPage(0);
  };

  const handleCampaignSelect = (event, value) => {
    setSelectedCampaign(value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatIST = (utcDate) => {
    return new Date(utcDate).toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "read":
      case "delivered":
        return "success";
      case "sent":
        return "info";
      case "queued":
        return "warning";
      default:
        return "error";
    }
  };

  return (
    <Container sx={{ py: 4 }}>
      <Typography variant="h5" gutterBottom>
        Track WhatsApp Campaigns
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <Autocomplete
            options={customerIds}
            loading={loadingCustomers}
            value={selectedCustomerId}
            onChange={handleCustomerSelect}
            size="small"
            renderInput={(params) => (
              <TextField
                {...params}
                label="Customer ID"
                variant="outlined"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingCustomers && (
                        <CircularProgress color="inherit" size={16} />
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          {errorCustomers && (
            <Typography color="error" variant="caption">
              Error: {customerError?.message || "Failed to load customer IDs"}
            </Typography>
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Autocomplete
            options={campaigns}
            loading={loadingCampaigns}
            value={selectedCampaign}
            onChange={handleCampaignSelect}
            disabled={!selectedCustomerId}
            size="small"
            renderInput={(params) => (
              <TextField
                {...params}
                label="Campaign"
                variant="outlined"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingCampaigns && (
                        <CircularProgress color="inherit" size={16} />
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          {errorCampaigns && (
            <Typography color="error" variant="caption">
              Error: {campaignError?.message || "Failed to load campaigns"}
            </Typography>
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <DateRangePicker
            startLabel="From"
            endLabel="To"
            onChange={(newRange) => {
              console.log(new Date(newRange.startDate));
              setDateRange(newRange);
            }}
            format="DD/MM/YYYY"
            showQuickSelect={true}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            placeholder="Search Phone"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            value={phoneS}
            variant="outlined"
            size="small"
            onChange={(e) => {
              const value = e.target.value;
              setPhoneS(value);
              debouncedPhoneS(value);
            }}
          />
        </Grid>
      </Grid>

      <Stack direction={"row"} gap={2}>
        {!!(selectedCustomerId && (selectedCampaign || isDateRangeS)) && (
          <CampaignStatsPanel
            cusId={selectedCustomerId}
            campaignName={selectedCampaign}
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
          />
        )}
        {selectedCustomerId && selectedCampaign && replyData && (
          <Chip
            label={`${replyData.replyCount} replies`}
            onClick={() => {
              const newTabUrl = `/campaign-chats/${selectedCustomerId}/${selectedCampaign}`;
              window.open(newTabUrl, "_blank");
            }}
            color={replyData.replyCount ? "success" : "warning"}
            sx={{ cursor: "pointer", mt: 1 }}
          />
        )}
      </Stack>

      <TableContainer component={Paper} sx={{ mt: 4 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Message ID</TableCell>
              <TableCell>Recipient</TableCell>
              <TableCell>Template</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Updated At</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loadingMessages && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            )}
            {errorMessages && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography color="error">
                    Error: {messageError?.message || "Failed to load messages"}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {!loadingMessages &&
              !errorMessages &&
              messagesData.data.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography>
                      {selectedCustomerId && selectedCampaign
                        ? "No messages found"
                        : "Select a customer and campaign to view messages"}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            {!loadingMessages &&
              !errorMessages &&
              messagesData.data.map((message) => (
                <TableRow key={message._id}>
                  <TableCell>{message._id}</TableCell>
                  <TableCell>{message.to}</TableCell>
                  <TableCell>{message.templateName}</TableCell>
                  <TableCell>
                    <Chip
                      label={message.status}
                      color={getStatusColor(message.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatIST(message.updatedOn)}</TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      {/* <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={messagesData.meta.total}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      /> */}

      <CustomPagination
        messagesData={messagesData}
        page={page}
        setPage={setPage}
        rowsPerPage={rowsPerPage}
      />
    </Container>
  );
};

export default TrackCampaign;
